/* eslint-disable no-restricted-globals */
import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, Snackbar, SnackbarCloseReason } from '@mui/material';
import { IVFormErrors, SelectNumber, VForm, VSwitch, VTextField, useVForm } from '../../../shared/forms';
import * as yup from 'yup';
import { EstoqueOperacoesService } from '../../../shared/services/api/estoque/estoqueOperacoes/EstoqueOperacoesService';
import { Environment } from '../../../shared/environment';
import { TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';

interface IFormData {
  descricao: string;
  tipo: number;
  estoque: boolean;
  tpag: number;
  faturamento: boolean;
  status: boolean;
}

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  open: boolean;
  onClose: (value?: string) => void;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  descricao: yup.string().required(),
  tipo: yup.number().required(),
  faturamento: yup.boolean().required(),
  estoque: yup.boolean().required(),
  status: yup.boolean().optional().default(true),
  tpag: yup.number().required(),

});

const OPERACOES_PROTEGIDAS = [
  Environment.URL_IDOPERACOES,
  Environment.URL_IDORCAMENTO,
  Environment.URL_IDVENDAPRAZO,
  Environment.URL_IDPIX,
  Environment.URL_IDCARTAO,
];

export const DialogOperacoes: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {

  const [id, setId] = useState('');
  const { onClose, value: valueProp, open, ...other } = props;
  const { formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      onClose(valueProp);
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!OPERACOES_PROTEGIDAS.includes(props.id)) {
      EstoqueOperacoesService.deleteById(props.id)
        .then(result => {
          if (result instanceof Error) {
            setOpenConfirm(false);
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setOpenConfirm(false);
            setTms('success');
            setMsg('Operação apagada com sucesso!');
            setOpenmsg(true);
          }
        });
    } else {
      setOpenConfirm(false);
      setTms('error');
      setMsg('Esta operação não pode ser excluída!');
      setOpenmsg(true);
    }


  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  const valorNovo = {
    descricao: '',
    tipooperacao: 0,
    tpag: 1,
    faturamento: false,
    estoque: true,
    venda: false,
    status: true,
    ajuste: false
  };

  useEffect(() => {
    if (props.id === 'novo') {
      formRef.current?.setData(valorNovo);
    }
  }, [id, formRef]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema.validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        if (props.id === 'novo') {
          EstoqueOperacoesService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else if (OPERACOES_PROTEGIDAS.includes(props.id)) {
          cancelarSalvamento();
          setTms('error');
          setMsg('Esta operação não pode ser alterada!');
          setOpenmsg(true);
        } else {
          EstoqueOperacoesService
            .updateById(props.id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  useEffect(() => {
    if (props.id !== 'novo') {
      if (open === true) {
        setIsLoading(true);
        EstoqueOperacoesService.getById(props.id)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              alert(result.message);
            } else {
              formRef.current?.setData(result);
            }
          });
      }
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setId(valueProp);
    }
  }, [valueProp, open]);

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando operação..."
        progress={saveProgress}
      />
      <Dialog
      open={open}
      {...other}
    >
      <DialogTitle>Operação</DialogTitle>
      <DialogContent dividers>
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
            <Alert
              onClose={handleCloseMsg}
              severity={tms}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {msg}
            </Alert>
          </Snackbar>
          <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert
              severity="warning"
              variant="filled"
              sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              action={
                <>
                  <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                    Sim
                  </Button>
                  <Button color="inherit" size="small" onClick={handleCancelDelete}>
                    Nâo
                  </Button>
                </>
              }
            >
              Tem certeza que deseja excluir este registro?
            </Alert>
          </Snackbar>
          <Grid container direction="column" padding={1} >
            <Grid container item direction="row" padding={1} spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={2} >
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='descricao'
                      disabled={isLoading}
                      label='Descrição'
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <SelectNumber

                      data={[
                        { nameBD: 0, descricao: 'Entrada' },
                        { nameBD: 1, descricao: 'Saída' },
                      ]}
                      disabled={isLoading}
                      label='Tipo de Operação'
                      name='tipo'
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <SelectNumber

                      data={[
                        { nameBD: 1, descricao: 'A Vista' },
                        { nameBD: 2, descricao: 'A prazo' },
                      ]}
                      disabled={isLoading}
                      label='Tipo de Pagamento'
                      name='tpag'
                    />
                  </Grid>

                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <VSwitch
                      name='faturamento'
                      label='Faturamento'
                      fdisabled={isLoading}
                      aliamento='start'
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>

                    <VSwitch
                      name='estoque'
                      label='Estoque'
                      fdisabled={isLoading}
                      aliamento='start'
                    />

                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    {(props.id !== 'novo') && (
                      <VSwitch
                        name='status'
                        label='Status'
                        fdisabled={isLoading}
                        aliamento='start'
                      />
                    )}
                  </Grid>


                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </VForm>
      </DialogContent>
      <DialogActions>
        {(props.id !== 'novo') && (
          <Button sx={{ marginRight: '5%', minWidth: '30%' }} color='error' startIcon={<Icon>delete</Icon>} variant="contained" onClick={handleDelete}>Excluir</Button>
        )}
        <Button sx={{ minWidth: '30%' }} variant='outlined' color='error' onClick={handleCancel}>Cancelar</Button>
        <Button sx={{ minWidth: '30%' }} variant='outlined' autoFocus disabled={isLoading || isSaving} onClick={save}>Confirmar</Button>
      </DialogActions>
    </Dialog>
    </>

  );
};